/**
 * Author: Andrey Morozov
 * Confidential. Proprietary code. Do not distribute without authorization.
 *
 * copilotSlice.ts — copilo\1 \2lice (services/operator-web-app).
 */

import { validateCopilotPayload, type CopilotEventType } from '@/realtime/copilotEventSchemas';

export interface TranscriptEntry {
  speaker: 'client' | 'operator' | 'bot';
  text: string;
  resultId: string;
  confidence?: number;
  isFinal: boolean;
}

export interface Hint {
  hintId: string;
  text: string;
  intent: string;
  confidence: number;
  why?: string;
  actions?: Array<'copy' | 'insert_note' | 'apply_field' | 'mark_used'>;
  eventId: string;
}

export type HighlightWidget =
  | 'customer_card_header'
  | 'customer_card_fields'
  | 'calendar_widget'
  | 'map_widget'
  | 'wrap_up_panel'
  | 'notes_widget'
  | 'meeting_summary';

export interface Highlight {
  targetWidget: HighlightWidget;
  level: 'soft' | 'medium' | 'critical';
  reason: string;
  ttlMs: number;
  expiresAt: number;
}

/** E6-S3: Min ms between applying new highlights (anti-flicker). Plan 09. */
export const HIGHLIGHT_THROTTLE_MS = 2000;

export interface CopilotState {
  partialText: string | null;
  partialSpeaker: 'client' | 'operator' | 'bot' | null;
  transcript: TranscriptEntry[];
  hints: Hint[];
  /** E6-S2: Hint IDs dismissed by operator (used/irrelevant). */
  dismissedHintIds: string[];
  highlight: Highlight | null;
  /** E6-S3: Last time we applied a highlight (for throttle). */
  lastHighlightAt: number;
  lastEventId: string | null;
}

export const initialCopilotState: CopilotState = {
  partialText: null,
  partialSpeaker: null,
  transcript: [],
  hints: [],
  dismissedHintIds: [],
  highlight: null,
  lastHighlightAt: 0,
  lastEventId: null,
};

const MAX_TRANSCRIPT = 500;
const MAX_HINTS = 20;

export function reduceCopilot(
  state: CopilotState,
  eventType: string,
  payload: Record<string, unknown>,
  eventId: string
): CopilotState {
  const copilotTypes: CopilotEventType[] = [
    'asr_partial',
    'asr_final',
    'hint_generated',
    'highlight_recommended',
  ];
  if (copilotTypes.includes(eventType as CopilotEventType)) {
    const result = validateCopilotPayload(eventType as CopilotEventType, payload);
    if (!result.ok) {
      if (typeof console !== 'undefined' && console.warn) {
        console.warn('[copilot] Invalid payload ignored', {
          eventType,
          eventId,
          reason: result.reason,
        });
      }
      return state;
    }
    payload = result.payload as unknown as Record<string, unknown>;
  }

  switch (eventType) {
    case 'asr_partial': {
      const p = payload as {
        speaker: 'client' | 'operator' | 'bot';
        text: string;
        result_id: string;
      };
      return {
        ...state,
        partialText: p.text,
        partialSpeaker: p.speaker,
        lastEventId: eventId,
      };
    }
    case 'asr_final': {
      const p = payload as {
        speaker: 'client' | 'operator' | 'bot';
        text: string;
        result_id: string;
        confidence?: number;
      };
      const entry: TranscriptEntry = {
        speaker: p.speaker,
        text: p.text,
        resultId: p.result_id,
        confidence: p.confidence,
        isFinal: true,
      };
      const transcript = [...state.transcript, entry].slice(-MAX_TRANSCRIPT);
      return {
        ...state,
        partialText: null,
        partialSpeaker: null,
        transcript,
        lastEventId: eventId,
      };
    }
    case 'hint_generated': {
      const p = payload as {
        hint_id: string;
        text: string;
        intent: string;
        confidence: number;
        why?: string;
        actions?: Array<'copy' | 'insert_note' | 'apply_field' | 'mark_used'>;
      };
      const hint: Hint = {
        hintId: p.hint_id,
        text: p.text,
        intent: p.intent,
        confidence: p.confidence,
        why: p.why,
        actions: p.actions,
        eventId,
      };
      const hints = [hint, ...state.hints].slice(0, MAX_HINTS);
      return { ...state, hints, lastEventId: eventId };
    }
    case 'highlight_recommended': {
      const p = payload as {
        target_widget: HighlightWidget;
        level: 'soft' | 'medium' | 'critical';
        reason: string;
        ttl_ms: number;
      };
      const now = Date.now();
      const throttleOk =
        state.lastHighlightAt === 0 || now - state.lastHighlightAt >= HIGHLIGHT_THROTTLE_MS;
      if (!throttleOk) return { ...state, lastEventId: eventId };
      return {
        ...state,
        highlight: {
          targetWidget: p.target_widget,
          level: p.level,
          reason: p.reason,
          ttlMs: p.ttl_ms,
          expiresAt: now + p.ttl_ms,
        },
        lastHighlightAt: now,
        lastEventId: eventId,
      };
    }
    default:
      return state;
  }
}
